import type { UserRole } from '@/types/domain';

/**
 * Table des routes de l'application.
 *
 * Les URL publiques sont en francais et lisibles : elles seront indexees par
 * Google (voir docs/ARCHITECTURE.md, section SEO). Les espaces prives sont
 * regroupes sous trois prefixes clairs, un par role.
 */
export const ROUTES = {
  home: '/',
  search: '/recherche',
  vehicle: (id = ':vehicleId') => `/vehicule/${id}`,
  cityLanding: (slug = ':citySlug') => `/location-voiture-${slug}`,
  becomePartner: '/devenir-partenaire',
  howItWorks: '/comment-ca-marche',

  /** Voitures a vendre : petites annonces, distinctes de la location. */
  listings: '/voitures-a-vendre',
  listing: (id = ':listingId') => `/voitures-a-vendre/${id}`,
  becomeSeller: '/vendre-ma-voiture',

  /**
   * Garages et lavages. Deux annuaires, une seule fiche.
   *
   * Deux URL d'entrée parce qu'on ne cherche pas un lavage comme on cherche un
   * mécanicien, et parce que « garage Abidjan » et « lavage auto Abidjan » sont
   * deux recherches distinctes sur Google. Mais un même établissement fait
   * souvent les deux : il n'a donc qu'une fiche, à une seule adresse.
   */
  garages: '/garages',
  carwashes: '/lavage-auto',
  provider: (slug = ':providerSlug') => `/services/${slug}`,
  becomeProvider: '/devenir-prestataire',

  /** Pages légales. Une URL par texte : elles se citent et se lient. */
  legal: {
    notice: '/mentions-legales',
    terms: '/conditions-generales',
    privacy: '/confidentialite',
  },

  booking: (vehicleId = ':vehicleId') => `/reservation/${vehicleId}`,
  bookingConfirmation: (bookingId = ':bookingId') => `/reservation/confirmation/${bookingId}`,

  signIn: '/connexion',
  signUp: '/inscription',
  forgotPassword: '/mot-de-passe-oublie',

  customer: {
    root: '/compte',
    bookings: '/compte/reservations',
    booking: (id = ':bookingId') => `/compte/reservations/${id}`,
    documents: '/compte/documents',
    payments: '/compte/paiements',
    messages: '/compte/messages',
    appointments: '/compte/rendez-vous',
    reviews: '/compte/avis',
    profile: '/compte/profil',
    settings: '/compte/parametres',
  },

  partner: {
    root: '/partenaire',
    vehicles: '/partenaire/vehicules',
    calendar: '/partenaire/calendrier',
    bookings: '/partenaire/reservations',
    booking: (id = ':bookingId') => `/partenaire/reservations/${id}`,
    vehicleEdit: (id = ':vehicleId') => `/partenaire/vehicules/${id}`,
    customers: '/partenaire/clients',
    revenue: '/partenaire/revenus',
    messages: '/partenaire/messages',
    appointments: '/partenaire/rendez-vous',
    profile: '/partenaire/profil',
    documents: '/partenaire/documents',
    settings: '/partenaire/parametres',
  },

  seller: {
    root: '/vendeur',
    listings: '/vendeur/annonces',
    listingNew: '/vendeur/annonces/nouvelle',
    listingEdit: (id = ':listingId') => `/vendeur/annonces/${id}`,
    messages: '/vendeur/messages',
    appointments: '/vendeur/rendez-vous',
    profile: '/vendeur/profil',
  },

  /** Garages et lavages : l'espace de celui qui reçoit les véhicules. */
  providerSpace: {
    root: '/prestataire',
    profile: '/prestataire/fiche',
    offerings: '/prestataire/prestations',
    appointments: '/prestataire/rendez-vous',
    account: '/prestataire/profil',
  },

  admin: {
    root: '/admin',
    users: '/admin/utilisateurs',
    partners: '/admin/agences',
    vehicles: '/admin/vehicules',
    bookings: '/admin/reservations',
    payments: '/admin/paiements',
    disputes: '/admin/litiges',
    listings: '/admin/annonces',
    providers: '/admin/prestataires',
    reports: '/admin/signalements',
    settings: '/admin/parametres',
  },
} as const;

export interface NavItem {
  label: string;
  to: string;
}

export const CUSTOMER_NAV: NavItem[] = [
  { label: 'Tableau de bord', to: ROUTES.customer.root },
  { label: 'Mes réservations', to: ROUTES.customer.bookings },
  { label: 'Mes documents', to: ROUTES.customer.documents },
  { label: 'Mes paiements', to: ROUTES.customer.payments },
  { label: 'Mes messages', to: ROUTES.customer.messages },
  { label: 'Mes rendez-vous', to: ROUTES.customer.appointments },
  { label: 'Mes avis', to: ROUTES.customer.reviews },
  { label: 'Profil', to: ROUTES.customer.profile },
  { label: 'Paramètres', to: ROUTES.customer.settings },
];

export const PARTNER_NAV: NavItem[] = [
  { label: 'Tableau de bord', to: ROUTES.partner.root },
  { label: 'Véhicules', to: ROUTES.partner.vehicles },
  { label: 'Calendrier', to: ROUTES.partner.calendar },
  { label: 'Réservations', to: ROUTES.partner.bookings },
  { label: 'Clients', to: ROUTES.partner.customers },
  { label: 'Revenus', to: ROUTES.partner.revenue },
  { label: 'Messages', to: ROUTES.partner.messages },
  { label: 'Rendez-vous garage', to: ROUTES.partner.appointments },
  { label: 'Profil agence', to: ROUTES.partner.profile },
  { label: 'Documents', to: ROUTES.partner.documents },
  { label: 'Paramètres', to: ROUTES.partner.settings },
];

export const SELLER_NAV: NavItem[] = [
  { label: 'Tableau de bord', to: ROUTES.seller.root },
  { label: 'Mes annonces', to: ROUTES.seller.listings },
  { label: 'Messages', to: ROUTES.seller.messages },
  { label: 'Mes rendez-vous', to: ROUTES.seller.appointments },
  { label: 'Profil', to: ROUTES.seller.profile },
];

export const PROVIDER_NAV: NavItem[] = [
  { label: 'Tableau de bord', to: ROUTES.providerSpace.root },
  { label: 'Rendez-vous', to: ROUTES.providerSpace.appointments },
  { label: 'Mes prestations', to: ROUTES.providerSpace.offerings },
  { label: 'Ma fiche', to: ROUTES.providerSpace.profile },
  { label: 'Mon profil', to: ROUTES.providerSpace.account },
];

export const ADMIN_NAV: NavItem[] = [
  { label: 'Tableau de bord', to: ROUTES.admin.root },
  { label: 'Utilisateurs', to: ROUTES.admin.users },
  { label: 'Agences', to: ROUTES.admin.partners },
  { label: 'Véhicules', to: ROUTES.admin.vehicles },
  { label: 'Réservations', to: ROUTES.admin.bookings },
  { label: 'Paiements', to: ROUTES.admin.payments },
  { label: 'Litiges', to: ROUTES.admin.disputes },
  { label: 'Annonces', to: ROUTES.admin.listings },
  { label: 'Garages & lavages', to: ROUTES.admin.providers },
  { label: 'Signalements', to: ROUTES.admin.reports },
  { label: 'Paramètres', to: ROUTES.admin.settings },
];

/**
 * Ou atterrit-on juste apres s'etre connecte.
 *
 * Un client arrive sur le catalogue : c'est ce qu'il vient chercher. Son espace
 * personnel reste a un clic, dans le menu. Une agence et un administrateur
 * arrivent directement dans leur outil de travail.
 */
export function landingRouteForRole(role: UserRole | null): string {
  switch (role) {
    case 'admin':
      return ROUTES.admin.root;
    case 'partner':
      return ROUTES.partner.root;
    case 'seller':
      return ROUTES.seller.root;
    case 'provider':
      return ROUTES.providerSpace.root;
    default:
      return ROUTES.home;
  }
}

/** L'espace prive correspondant au role, cible du bouton « Mon espace ». */
export function spaceRouteForRole(role: UserRole | null): string {
  switch (role) {
    case 'admin':
      return ROUTES.admin.root;
    case 'partner':
      return ROUTES.partner.root;
    case 'seller':
      return ROUTES.seller.root;
    case 'provider':
      return ROUTES.providerSpace.root;
    case 'customer':
      return ROUTES.customer.root;
    default:
      return ROUTES.home;
  }
}

/* -------------------------------------------------------------------------- */
/* Titres de page                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Nom lisible de chaque écran.
 *
 * Sert à la barre mobile : sur un téléphone, la colonne latérale qui portait le
 * nom de la section est masquée, et l'utilisateur ne sait plus où il est.
 *
 * Les segments dynamiques sont notés `:` — la correspondance se fait segment
 * par segment, donc `/vehicule/abc-123` retombe sur `/vehicule/:id`.
 */
const PAGE_TITLES: [string, string][] = [
  ['/', 'Accueil'],
  ['/recherche', 'Rechercher un véhicule'],
  ['/vehicule/:id', 'Fiche du véhicule'],
  ['/voitures-a-vendre', 'Voitures à vendre'],
  ['/voitures-a-vendre/:id', 'Annonce'],
  ['/vendre-ma-voiture', 'Vendre ma voiture'],
  ['/garages', 'Garages mécaniques'],
  ['/lavage-auto', 'Lavage auto'],
  ['/services/:slug', 'Fiche du prestataire'],
  ['/devenir-prestataire', 'Inscrire mon garage'],
  ['/comment-ca-marche', 'Comment ça marche'],
  ['/devenir-partenaire', 'Devenir partenaire'],
  ['/reservation/:id', 'Réservation'],
  ['/reservation/confirmation/:id', 'Réservation confirmée'],
  ['/connexion', 'Connexion'],
  ['/inscription', 'Créer un compte'],
  ['/mot-de-passe-oublie', 'Mot de passe oublié'],
  ['/mentions-legales', 'Mentions légales'],
  ['/conditions-generales', 'Conditions générales'],
  ['/confidentialite', 'Confidentialité'],

  ['/compte', 'Mon espace'],
  ['/compte/reservations', 'Mes réservations'],
  ['/compte/reservations/:id', 'Détail de la réservation'],
  ['/compte/documents', 'Mes documents'],
  ['/compte/paiements', 'Mes paiements'],
  ['/compte/messages', 'Mes messages'],
  ['/compte/rendez-vous', 'Mes rendez-vous'],
  ['/compte/avis', 'Mes avis'],
  ['/compte/profil', 'Mon profil'],
  ['/compte/parametres', 'Paramètres'],

  ['/partenaire', 'Tableau de bord'],
  ['/partenaire/vehicules', 'Mes véhicules'],
  ['/partenaire/vehicules/:id', 'Fiche véhicule'],
  ['/partenaire/calendrier', 'Calendrier'],
  ['/partenaire/reservations', 'Réservations'],
  ['/partenaire/reservations/:id', 'Détail de la réservation'],
  ['/partenaire/clients', 'Clients'],
  ['/partenaire/revenus', 'Revenus'],
  ['/partenaire/messages', 'Messages'],
  ['/partenaire/rendez-vous', 'Rendez-vous garage'],
  ['/partenaire/profil', 'Profil de l’agence'],
  ['/partenaire/documents', 'Documents'],
  ['/partenaire/parametres', 'Paramètres'],

  ['/vendeur', 'Espace vendeur'],
  ['/vendeur/annonces', 'Mes annonces'],
  ['/vendeur/annonces/nouvelle', 'Nouvelle annonce'],
  ['/vendeur/annonces/:id', 'Modifier l’annonce'],
  ['/vendeur/messages', 'Messages'],
  ['/vendeur/rendez-vous', 'Mes rendez-vous'],
  ['/vendeur/profil', 'Mon profil'],

  ['/prestataire', 'Espace prestataire'],
  ['/prestataire/rendez-vous', 'Rendez-vous'],
  ['/prestataire/prestations', 'Mes prestations'],
  ['/prestataire/fiche', 'Ma fiche'],
  ['/prestataire/profil', 'Mon profil'],

  ['/admin', 'Administration'],
  ['/admin/utilisateurs', 'Utilisateurs'],
  ['/admin/agences', 'Agences'],
  ['/admin/vehicules', 'Véhicules'],
  ['/admin/reservations', 'Réservations'],
  ['/admin/paiements', 'Paiements'],
  ['/admin/litiges', 'Litiges'],
  ['/admin/annonces', 'Annonces à vérifier'],
  ['/admin/prestataires', 'Garages & lavages'],
  ['/admin/signalements', 'Signalements'],
  ['/admin/parametres', 'Paramètres'],
];

function segmentsMatch(pattern: string, path: string): boolean {
  const a = pattern.split('/').filter(Boolean);
  const b = path.split('/').filter(Boolean);
  if (a.length !== b.length) return false;
  return a.every((segment, index) => segment.startsWith(':') || segment === b[index]);
}

/** Titre de la page courante, ou `null` si la route est inconnue. */
export function titleForPath(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  // Une correspondance exacte l'emporte toujours sur un motif dynamique :
  // « /vendeur/annonces/nouvelle » n'est pas « modifier l'annonce ».
  const exact = PAGE_TITLES.find(([pattern]) => pattern === clean);
  if (exact) return exact[1];
  const dynamic = PAGE_TITLES.find(([pattern]) => segmentsMatch(pattern, clean));
  return dynamic ? dynamic[1] : null;
}

/** Les pages de villes : « /location-voiture-abidjan ». */
export function isCityLanding(pathname: string): boolean {
  return /^\/location-voiture-[a-z-]+$/.test(pathname);
}
